import { Injectable, ElementRef, Renderer2, RendererFactory2 } from '@angular/core';

@Injectable({
  providedIn: 'root'
})
export class TableTooltipService {
  private renderer: Renderer2;

  constructor(private rendererFactory: RendererFactory2) {
    this.renderer = this.rendererFactory.createRenderer(null, null);
  }

  /**
   * Initialize tooltips for all table cells in the document
   */
  initializeTableTooltips(): void {
    console.log('Initializing table tooltips...');
    // Wait for DOM to be ready
    setTimeout(() => {
      this.applyTooltipsToAllTables();
    }, 500);
  }

  /**
   * Apply tooltips to all table cells
   */
  private applyTooltipsToAllTables(): void {
    const tableSelectors = [
      '.mdc-data-table__cell',
      'mat-cell.mat-cell',
      '.mat-cell',
      'td'
    ];

    let totalCells = 0;
    let tooltipCells = 0;

    tableSelectors.forEach(selector => {
      const cells = document.querySelectorAll(selector);
      totalCells += cells.length;
      
      cells.forEach(cell => {
        const applied = this.applyTooltipToCell(cell as HTMLElement);
        if (applied) tooltipCells++;
      });
    });

    console.log(`Table tooltips applied: ${tooltipCells}/${totalCells} cells`);
  }

  /**
   * Apply tooltip to a specific cell if text is truncated
   */
  private applyTooltipToCell(cell: HTMLElement): boolean {
    // Skip cells that already have tooltips or are action cells
    if (cell.hasAttribute('matTooltip') || 
        cell.hasAttribute('title') ||
        cell.classList.contains('action-cell') ||
        cell.classList.contains('user-cell') ||
        cell.classList.contains('image-cell') ||
        cell.querySelector('button') ||
        cell.querySelector('img') ||
        cell.querySelector('mat-select')) {
      return false;
    }

    // Check if text is truncated
    const isTextTruncated = cell.scrollWidth > cell.clientWidth;
    
    if (isTextTruncated) {
      const fullText = cell.textContent?.trim() || cell.innerText?.trim() || '';
      if (fullText && fullText.length > 10) { // Only add tooltip for meaningful text
        
        // Set multiple tooltip attributes for better compatibility
        this.renderer.setAttribute(cell, 'matTooltip', fullText);
        this.renderer.setAttribute(cell, 'matTooltipPosition', 'above');
        this.renderer.setAttribute(cell, 'matTooltipClass', 'custom-tooltip');
        this.renderer.setAttribute(cell, 'title', fullText);
        this.renderer.addClass(cell, 'auto-tooltip-enabled');
        
        // Add data attributes for debugging
        this.renderer.setAttribute(cell, 'data-tooltip', 'true');
        this.renderer.setAttribute(cell, 'data-tooltip-text', fullText);
        
        console.log('Applied tooltip to cell:', fullText.substring(0, 50) + '...');
        return true;
      }
    }
    
    return false;
  }

  /**
   * Apply tooltips to a specific table element
   */
  applyTooltipsToTable(tableElement: HTMLElement): void {
    const cells = tableElement.querySelectorAll('.mdc-data-table__cell, mat-cell.mat-cell, .mat-cell, td');
    let tooltipCells = 0;
    
    cells.forEach(cell => {
      const applied = this.applyTooltipToCell(cell as HTMLElement);
      if (applied) tooltipCells++;
    });
    
    console.log(`Applied tooltips to ${tooltipCells} cells in table`);
  }

  /**
   * Refresh tooltips for all tables (useful after data changes)
   */
  refreshTooltips(): void {
    console.log('Refreshing table tooltips...');
    
    // Remove existing auto-tooltips
    const existingTooltips = document.querySelectorAll('.auto-tooltip-enabled');
    existingTooltips.forEach(cell => {
      this.renderer.removeAttribute(cell, 'matTooltip');
      this.renderer.removeAttribute(cell, 'matTooltipPosition');
      this.renderer.removeAttribute(cell, 'matTooltipClass');
      this.renderer.removeAttribute(cell, 'title');
      this.renderer.removeAttribute(cell, 'data-tooltip');
      this.renderer.removeAttribute(cell, 'data-tooltip-text');
      this.renderer.removeClass(cell, 'auto-tooltip-enabled');
    });

    // Reapply tooltips
    setTimeout(() => {
      this.applyTooltipsToAllTables();
    }, 100);
  }
} 